import { PossibleBusinessCard } from '@app/store/reducers/salaryCards/businessCardOrder.api'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// Interfaces
interface AvailablePaymentSystem {
  main: boolean
  mastercard: boolean
  visa: boolean
}

interface CardList {
  value: string
  label: string
}

interface SelectedCard {
  paymentSystem: 'visa' | 'mastercard'
  cardId: string
  cardName: string
  issueFee: number
}

interface CardDurationField {
  value: number
  label: string // example 1 year | 2 year
  fee: number,
  cardTariffId: number,
}

export interface CardDuration {
  year: number
  amount: number
  cardTariffId: number
}

// usePossibleCard Hook
export const usePossibleCard = () => {
  const [selectedPaymentSystem, setSelectedPaymentSystem] = useState<
    'visa' | 'mastercard' | null
  >(null)
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [apiData, setApiData] = useState<PossibleBusinessCard | null>(null)
  const [cardDuration, setCardDuration] = useState<CardDurationField[]>([])

  const { t } = useTranslation()
  const lang = localStorage.getItem("i18nextLng")

  // API verisini set etmek için
  const setPossibleCard = (data: PossibleBusinessCard) => {
    setApiData(data)
  }

  // Available payment systems
  const availablePaymentSystems: AvailablePaymentSystem = useMemo(() => {
    if (!apiData) {
      return { main: false, mastercard: false, visa: false }
    }

    return {
      main: apiData.main,
      mastercard: apiData.masterCardAvailable,
      visa: apiData.visaAvailable,
    }
  }, [apiData])

  // Card list - seçilen payment system'e göre filtrelenir
  const cardList: CardList[] = useMemo(() => {
    if (!apiData) return []

    const allCards = [
      ...apiData.masterCardList.map((card) => ({
        paymentSystem: 'mastercard' as const,
        cardId: card.cardId.toString(),
        cardName: card.cardName,
        issueFee: card.issueFee,
      })),
      ...apiData.visaList.map((card) => ({
        paymentSystem: 'visa' as const,
        cardId: card.cardId.toString(),
        cardName: card.cardName,
        issueFee: card.issueFee,
      })),
    ]

    // Default olarak selectedPaymentSystem'e göre filtrele
    const filteredCards = selectedPaymentSystem
      ? allCards.filter((card) => card.paymentSystem === selectedPaymentSystem)
      : allCards

    // Sadece cardId ve cardName return et
    return filteredCards.map((card) => ({
      value: card.cardId,
      label: card.cardName,
    }))
  }, [apiData, selectedPaymentSystem])

  useMemo(() => {
    if (!apiData || !selectedCardId) return []

    // Seçili kartı bul
    const allCards = [...apiData.masterCardList, ...apiData.visaList]

    const selectedCardData = allCards.find(
      (card) => card.cardId.toString() === selectedCardId
    )

    if (!selectedCardData) return []

    const d = selectedCardData.cardDuration
      .map((duration) => ({
        value: duration.year,
        fee: duration.amount,
        cardTariffId: duration.cardTariffId,
        label: `${duration.year} ${t('businessCardOrders.labels.year')}`,
      }));

    setCardDuration(d)
  }, [apiData, selectedCardId, lang])

  const selectedCard: SelectedCard | null = useMemo(() => {
    if (!apiData || !selectedCardId) return null

    const allCards = [
      ...apiData.masterCardList.map((card) => ({
        paymentSystem: 'mastercard' as const,
        cardId: card.cardId.toString(),
        cardName: card.cardName,
        issueFee: card.issueFee,
      })),
      ...apiData.visaList.map((card) => ({
        paymentSystem: 'visa' as const,
        cardId: card.cardId.toString(),
        cardName: card.cardName,
        issueFee: card.issueFee,
      })),
    ]

    return allCards.find((card) => card.cardId === selectedCardId) || null
  }, [apiData, selectedCardId])

  // Payment system seçme fonksiyonu
  const setPaymentSystem = (paymentSystem: 'visa' | 'mastercard' | null) => {
    setSelectedPaymentSystem(paymentSystem?.toLocaleLowerCase())
  }

  const setSelectedCard = (cardId: string | null) => {
    setSelectedCardId(cardId)
  }

  const cardImage = selectedCardId ? apiData?.cardImages?.[selectedCardId] : null

  return {
    cardImage,
    availablePaymentSystems,
    cardList,
    cardDuration,
    selectedPaymentSystem,
    selectedCard,
    setPaymentSystem,
    setPossibleCard,
    setSelectedCard,
    setCardDuration
  }
}
